import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { IDatasetLensColumn } from '../reportDataset';
import {
  FilterableColumnTypes,
  IDimensionColumn,
  IDimensionColumnReference,
  ILensColumnReference,
  IProjection,
  IRelation
} from './accountSummaryColumns';
import { IAccountSummaryChart } from './accountSummaryCharts';
import { RelativeFilter, IRollingNumRangeOption } from './accountSummaryRelativeFilters';
import type * as AccountSummaryColumnFilters from './accountSummaryColumnFilters';
import { FilterOperators, InSetFilterOperator } from '../reportFilters';
import type { MultiHierarchyReportFilter } from './multiHierarchyFilters';
import type { MhDetailsViewConfig } from './multiHierarchyDetails';
import type { PublicMhDrillCapability } from './publicMhDrillCapabilities';

export interface IExpandedGroupKeys {
  [index: string]: IExpandedGroupKeys;
}

export interface IExpandedRows {
  [key: string]: IExpandedRows;
}

/**
 * Composition rule for the cell where a calculated row meets a calculated column.
 *
 * - ColOfRow (default): apply the calculated-column formula over the calculated row's own
 *   per-column cells, i.e. colFormula(row(c1), row(c2), ...).
 * - RowOfCol: apply the calculated-row formula over each operand row's calculated-column
 *   values, i.e. rowFormula(col(opA), col(opB)).
 *
 * For linear formulas (+, -) the two are equivalent; they only diverge for non-linear
 * formulas (x, /, variance %). Applies to both single- and multi-hierarchy reports.
 */
export enum CalcColIntersectionMode {
  /** Do not compute the intersection; render an en-dash (default). */
  DoNotCalculate = 'COL_INTERSECTION_DO_NOT_CALCULATE',
  /** Apply the column formula over the calculated row's own data-column cells. */
  ColOfRow = 'COL_INTERSECTION_CALC_COL_OF_ROW',
  /** Apply the row formula over each operand row's calculated-column values. */
  RowOfCol = 'COL_INTERSECTION_CALC_ROW_OF_COL'
}

/** Effective default: do not calculate the intersection — the user must opt in explicitly. */
export const DEFAULT_CALC_COL_INTERSECTION_MODE: CalcColIntersectionMode = CalcColIntersectionMode.DoNotCalculate;

export interface IAccountSummaryFormatting {
  currencyRounding: EAccountSummaryRoundingOptions;
  numberRounding: EAccountSummaryRoundingOptions;
  percentRounding: EAccountSummaryRoundingOptions;
  negativeNumberDisplay: EAccountSummaryRoundingOptions;
  zeroNumbers: IZeroNumbersCheckboxOption[];
  showCurrencySymbol?: boolean;
  /**
   * How the calculated-row x calculated-column intersection cell is composed.
   * When undefined, {@link DEFAULT_CALC_COL_INTERSECTION_MODE} (do-not-calculate — en-dash) applies.
   */
  calcColIntersectionMode?: CalcColIntersectionMode;
}

export type IAccountSummaryRoundingScale = 0 | 4;

export enum IAccountSummaryRoundingRule {
  RoundToNearest = 'roundToNearest',
  RoundUp = 'roundUp'
}

export interface IAccountSummaryRounding {
  scale: IAccountSummaryRoundingScale;
  rule: IAccountSummaryRoundingRule;
}

export enum EAccountSummaryVariant {
  MultiHierarchy = 'multi-hierarchy'
}

export interface IAccountSummary {
  variant?: EAccountSummaryVariant;
  columnGroups: IColumnSource[];
  columnOrder?: Record<string, number>;
  filters: AccountSummaryColumnFilters.IColumnFilterMDS<IDimensionColumn>[];
  formatting?: IAccountSummaryFormatting;
  rounding?: IAccountSummaryRounding;
  rowGroups: IRowSource[];
  expandedRows: IExpandedRows;
  asOfDate?: string;
  hiddenColumns?: Record<string, boolean>;
  charts?: IAccountSummaryChart[];
  hideColumnGroupHeaders?: boolean;
  /**
   * @deprecated expandedGroupKeys has been replaced by expandedRows
   */
  expandedGroupKeys?: IExpandedGroupKeys;
  /**
   * When false, row-group total rows are hidden in the grid (multi-hierarchy account summary only).
   * Undefined/true preserves existing behavior (totals shown). Ignored for non-multi-hierarchy reports.
   */
  showTotalsRows?: boolean;
  /**
   * Shows first-level breakdown rows at the root level — in the grid, in PDF/CSV exports and in the
   * Details table (multi-hierarchy account summary only). Ignored for non-multi-hierarchy reports.
   *
   * Only an explicit `true` shows them: every consumer tests `=== true`, and the default written by
   * `createMultiHierarchyAccountSummary` is `false`, so an absent flag hides them. Note this is the
   * opposite default to `showTotalsRows` above, which tests `!== false` and therefore shows totals
   * when absent — the two are not interchangeable.
   *
   * A root-level breakdown row aggregates the same accounts as the row group's root, so the flag
   * decides whether those accounts are represented once or twice; the Details table drops such rows
   * from its walk when it is off, to avoid emitting every account twice.
   */
  showRootLevelBreakdowns?: boolean;
  /**
   * Details-tab configuration (BUD-18475) — visible columns, order, filters, sort, Show Totals.
   * Absent until a user changes something on the Details tab.
   *
   * Multi-hierarchy account summaries only, and declared here rather than on
   * `MultiHierarchyAccountSummary` for the same reason as the two flags above: shared code that
   * carries display state across a saved-view update reads it off the union, which cannot see a
   * property that exists on only one arm.
   */
  details?: MhDetailsViewConfig;
  /**
   * Exact child-drill capabilities allowed for anonymous public MH viewers (BUD-18770).
   * Absent/empty means root-only on the public API. UI restore still uses `expandedRows`.
   */
  publicDrillCapabilities?: PublicMhDrillCapability[];
}

export interface ILensColumnMap {
  [columnGroupId: string]: IDatasetLensColumn[];
}

export interface ITableExpressionsDefinition {
  [alias: string]: string;
}

export const HiddenColumnGroupIndicator = 'Header\u00A0Hidden';

export type IColumnGroupDateFilter =
  readonly AccountSummaryColumnFilters.ISingleValueColumnFilter<ILensColumnReference>[];

export enum EAccountSummaryRoundingOptions {
  Hundredths = -2,
  Tenths = -1,
  Thousands = 3,
  Hundreds = 2,
  Tens = 1,
  Ones = 0,
  Standard = '-1',
  Accounting = '(1)'
}

export enum EZeroNumbersCheckboxOptions {
  Rows = 'rows',
  Columns = 'columns',
  Display = 'display'
}

export interface IZeroNumbersCheckboxOption {
  label: string;
  enabled: boolean;
}

export enum EGroupType {
  Expression = 'expression',
  /** Column group data source */
  Lens = 'lens',
  /** Row group data source */
  COA = 'coa',
  Override = 'override',
  MultiHierarchy = 'multi-hierarchy',
  /**
   * Row group data source — a multi-hierarchy override row. Deliberately distinct from
   * `Override` so that `isRowOverride()` (a bare `type ===` check) never matches it and
   * record factories are forced to handle it explicitly.
   */
  MultiHierarchyOverride = 'multi-hierarchy-override',
  MultiHierarchyCalculated = 'multi-hierarchy-calculated',
  MultiHierarchyCalculatedColumn = 'multi-hierarchy-calculated-column'
}

export interface ColumnDefinition extends Partial<ColGroupDef> {
  children?: CustomColDef[];
  suppressMovable: boolean;
  field?: string;
}

export interface CustomColDef extends ColDef {
  display: string;
  type: string;
}

export type ColumnValueSegment = string | number | null;
export type ColumnValue = ColumnValueSegment[];

export type RowGroupReference = string;
export type IGroup = IRowSource | IColumnSource;

export enum ColumnOperandType {
  Offset = 'offset',
  Value = 'value'
}

export interface ColumnOperandOffsetIdentifer {
  type: ColumnOperandType.Offset;
  groupId: string;
  offset: number;
  agGridColOffset?: number;
}
export interface ColumnOperandValueIdentifier {
  type: ColumnOperandType.Value;
  groupId: string;
  value: ColumnValueSegment;
}

export type ColumnOperandIdentifier = ColumnValue | ColumnOperandOffsetIdentifer | ColumnOperandValueIdentifier | null;
export type OperandIdentifier = ColumnOperandIdentifier | RowGroupReference | null;

export enum EExpressionDisplayType {
  Currency = 'currency',
  Number = 'number',
  Percent = 'percent'
}

export interface IGroupExpression<Operand extends OperandIdentifier = OperandIdentifier> {
  id: string;
  type: EGroupType.Expression;
  expression: string;
  display?: EExpressionDisplayType;
  relativeFilter?: RelativeFilter;
  rollingNumRangeOption?: IRollingNumRangeOption;
  dateFilter?: IColumnGroupDateFilter;
  operands: {
    [operatorName: string]: Operand;
  };
  alias: string;
  /**
   * @deprecated columnAlias has been replaced with alias and should not be used
   */
  columnAlias?: string;
  /**
   * @deprecated calculation is no longer used as it was for a ui that no longer exists
   */
  calculation?: string;
}

// Stores dependant calculated row groups info
export interface IAccountSummaryDependantRowGroupsInfo {
  dependantRowGroups: IGroupExpression[];
  isSelectedRowGroupDepended: boolean;
  isLastOne: boolean;
}

export enum ELedgerType {
  ex = 'ex',
  re = 're',
  as = 'as',
  li = 'li',
  eq = 'eq',
  ft = 'ft',
  un = 'un'
}

export enum ELedgerTypeDisplay {
  ex = 'Expenses',
  re = 'Revenues',
  as = 'Assets',
  li = 'Liabilities',
  eq = 'Equities',
  ft = 'FTEs',
  un = 'Uncategorized'
}

// Composite All Ledger Type
export enum EAllLedgerType {
  al = 'al'
}

export enum EAllLedgerTypeDisplay {
  al = 'All'
}

export type UnifiedLedgerType = ELedgerType | EAllLedgerType;

export type UnifiedLedgerTypeDisplay = ELedgerTypeDisplay | EAllLedgerTypeDisplay;

/**
 * IDimensionColumn
 * IProjection
 * IColumnGroupColumn
 */
export interface IGroupingSet<ColumnType extends FilterableColumnTypes> {
  grouping: ColumnType[];
  filters: AccountSummaryColumnFilters.IColumnFilterMDS<ColumnType>[];
  id?: string;
}

export interface IRowGroup<LedgerType = UnifiedLedgerType>
  extends IGroupingSet<IDimensionColumn | IDimensionColumnReference | ILensColumnReference> {
  id?: string;
  showOnly: AccountSummaryColumnFilters.ISingleValueColumnFilter<IDimensionColumn | IDimensionColumnReference> & {
    operator: FilterOperators.Equal | InSetFilterOperator.InSet;
    value: LedgerType;
  };
  groupName: string;
  type?: EGroupType.COA;
}

export type IRowGroupLike = IRowOverride | IRowGroup | MultiHierarchyRowGroup | MultiHierarchyRowOverride;

export interface IRowGrouping {
  dimension?: IRelation;
  columnName: string;
  operator: FilterOperators;
  value: string;
}
export interface IColumnGroup extends IGroupingSet<IProjection> {
  dateFilter?: IColumnGroupDateFilter;
  relativeFilter?: RelativeFilter;
  rollingNumRangeOption?: IRollingNumRangeOption;
  dateColumnName?: string;
}

export interface IDataSource {
  lens: IRelation;
  dateColumnName?: string;
  relativeFilter?: RelativeFilter;
  rollingNumRangeOption?: IRollingNumRangeOption;
  // We probably don't need this as it is defined on the lens via semantic type:
  measureColumn: ILensColumnReference;
}

export interface IRowOverride extends Omit<IRowGroup, 'type'> {
  id?: string;
  type: EGroupType.Override;
  columnGroups: Record<string, IColumnDataSource>;
}

/**
 * Several source groups rendered as ONE row whose figures aggregate all of them.
 *
 * Exists for combined (multi-entity) reports, where the same logical group is a separate group id
 * in each member entity — two rows both called "Funds-Reporting" is not what a consolidated
 * statement wants. Nothing restricts it to cross-entity use, because the aggregation is entirely a
 * grouping question and the query does not care which entity a source id came from.
 */
export interface MultiHierarchyMergedGroup {
  /**
   * Stable identity, persisted with the report. It is the row's key in the query result, so
   * drill-down sends it back — a value generated per render could not survive a save or an expand.
   */
  id: string;
  /** The row label. Replaces the source groups' own names. */
  name: string;
  /** The source group ids this row aggregates. Must be a subset of the level's `groupIds`. */
  groupIds: string[];
}

/**
 * How far into a breakdown level's group hierarchy the report drills (BUD-19598).
 *
 * - `full`: the group, its sub-groups, then the next level or the leaf detail — today's behaviour.
 * - `rollup`: the group's own sub-groups are never drilled. With a deeper level configured, that
 *   level's rows attach directly beneath the group row; on the deepest level the row is a total
 *   with nothing beneath it.
 * - `leaf`: the group expands straight into its leaf detail — its segment codes, or its accounts
 *   when Account Number is the next level — skipping the sub-groups. Offered on the leaf-detail
 *   level only (`isMultiHierarchyLeafDetailLevel`).
 *
 * Honoured on every CoA segment level of a row group whose first level is CoA
 * (`isMultiHierarchyDepthLevel`); totals never change, only which rows render.
 */
export const MULTI_HIERARCHY_DEPTH_OPTIONS = ['full', 'rollup', 'leaf'] as const;
export type MultiHierarchyDepthOption = (typeof MULTI_HIERARCHY_DEPTH_OPTIONS)[number];
export const DEFAULT_MULTI_HIERARCHY_DEPTH: MultiHierarchyDepthOption = 'full';

/**
 * How a leaf code row of a breakdown level is labelled (BUD-19598).
 *
 * - `codeDescription`: `200 - POLICE` — today's behaviour.
 * - `description`: `POLICE`.
 * - `none`: the code rows are not shown at all (their group rows become terminal).
 */
export const MULTI_HIERARCHY_CODE_DISPLAY_OPTIONS = ['codeDescription', 'description', 'none'] as const;
export type MultiHierarchyCodeDisplayOption = (typeof MULTI_HIERARCHY_CODE_DISPLAY_OPTIONS)[number];
export const DEFAULT_MULTI_HIERARCHY_CODE_DISPLAY: MultiHierarchyCodeDisplayOption = 'codeDescription';

export interface MultiHierarchyGroupingLevel {
  segmentId: string;
  /**
   * Every group id this level references, merged or not.
   *
   * Merging does NOT remove ids from here: this list is what scopes the rows query and what every
   * existing reader (entity scope, deleted-group validation, row-label resolution) consumes, so a
   * merge stays invisible to all of them. `mergedGroups` is a grouping instruction layered over
   * this set, not a replacement for part of it.
   */
  groupIds: string[];
  /**
   * Groups to render as single aggregated rows. Absent for every report that has never merged.
   *
   * Each entry's `groupIds` must be a subset of this level's `groupIds`, and an id must not appear
   * in more than one entry — otherwise its measures would be counted into two rows.
   */
  mergedGroups?: MultiHierarchyMergedGroup[];
  /** Set for non-CoA text column breakdowns; mutually exclusive with segmentId/groupIds. */
  columnName?: string;
  /**
   * Set for a leaf-level "break down by Account Number" grouping. Retains
   * `segmentId`/`groupIds` to scope which accounts are enumerated, but the
   * pipeline groups by `account_number_id` (with the account-number string
   * column and its description) instead of the segment's `child_group_id`.
   * Account-number levels are terminal: they are always the last/deepest
   * level of a row group.
   */
  accountNumber?: true;
  /**
   * Depth setting for this level's group hierarchy. Absent means `full` (today's behaviour), so a
   * report that never touched the setting persists exactly as before. Read on every CoA segment
   * level of a CoA-first row group (`isMultiHierarchyDepthLevel`); `leaf` only on the leaf-detail
   * level (`isMultiHierarchyLeafDetailLevel`); ignored anywhere else.
   */
  hierarchyDepth?: MultiHierarchyDepthOption;
  /**
   * Drops this level's own group row, so its leaf detail — segment codes, or accounts when Account
   * Number follows — lists directly under the parent row ("Revenue broken down by Department
   * codes"). Read only with `hierarchyDepth: 'leaf'` on a breakdown level (index ≥ 1); absent means
   * the group row renders. See `isMultiHierarchyGroupRowHideable`.
   */
  hideGroupRow?: true;
  /**
   * How this level's leaf code rows are labelled, or `none` to hide them. Absent means
   * `codeDescription`. Read only on the deepest configured CoA segment level; irrelevant under
   * `hierarchyDepth: 'rollup'` (no codes render) and coerced away from `none` under `leaf` (nothing
   * would render) — see `resolveMultiHierarchyLevelDisplay`.
   */
  codeDisplay?: MultiHierarchyCodeDisplayOption;
}

export interface MultiHierarchyRowGroup
  extends Omit<IRowGroup, 'type' | 'showOnly' | 'grouping' | 'filters' | 'groupName'> {
  id?: string;
  displayName?: string;
  groupDisplayNames?: Record<string, string>;
  type: EGroupType.MultiHierarchy;
  grouping: MultiHierarchyGroupingLevel[];
  /** CoA-segment filters, and/or non-CoA lens-column filters once a UI writes them here. */
  filters?: MultiHierarchyReportFilter[];
}

/**
 * An ERP (multi-hierarchy) override row: a row whose per-column data comes from a
 * different lens than the report's parent column groups, while still drilling down
 * like a normal multi-hierarchy row group.
 *
 * Mirrors the non-ERP precedent `IRowOverride extends Omit<IRowGroup, 'type'>`. Override
 * rows must remain drillable, so `grouping` is inherited rather than omitted.
 */
export interface MultiHierarchyRowOverride extends Omit<MultiHierarchyRowGroup, 'type'> {
  type: EGroupType.MultiHierarchyOverride;
  /** Keyed by parent column group id. */
  columnGroups: Record<string, MultiHierarchyColumnDataSource>;
}

export enum AggregationType {
  SUM = 'sum',
  NONE = 'none'
}

export interface IColumnValueMapping {
  [key: string]: string | undefined | IColumnValueMapping;
}

export type IColumnDataSource = IColumnGroup &
  IDataSource & {
    type: EGroupType.Lens | EGroupType.MultiHierarchy;
    id: string;
    columnValueMapping?: IColumnValueMapping;
  };

export type IColumnAggregation = IColumnDataSource & { aggregation: AggregationType };

export type MultiHierarchyColumnDataSource = Omit<IColumnGroup, 'filters'> &
  IDataSource & {
    type: EGroupType.MultiHierarchy;
    id: string;
    showTotalsColumn?: boolean;
    columnValueMapping?: IColumnValueMapping;
    /**
     * CoA-segment filters and/or non-CoA lens-column filters scoping this column group. Row-group
     * and report-level (view) filters remain CoA-only for now — the query pipelines tolerate lens
     * entries there, but no UI writes them.
     */
    filters?: MultiHierarchyReportFilter[];
    relativeRolling?: string;
  };

export type IColumnSource =
  | IColumnDataSource
  | IGroupExpression<ColumnOperandIdentifier>
  | IColumnAggregation
  | MultiHierarchyColumnDataSource
  | MultiHierarchyCalculatedColumnGroup;

export interface MultiHierarchyCalculatedRowGroup {
  id: string;
  type: EGroupType.MultiHierarchyCalculated;
  formula: string;
  alias: string;
  display?: EExpressionDisplayType;
}

export type MultiHierarchyCalculatedColumnGroup = {
  id: string;
  type: EGroupType.MultiHierarchyCalculatedColumn;
  formula: string;
  alias: string;
  display?: EExpressionDisplayType;
  hidden?: boolean;
};

export type IRowSource =
  | IRowGroup
  | IGroupExpression
  | IRowOverride
  | MultiHierarchyRowGroup
  | MultiHierarchyRowOverride
  | MultiHierarchyCalculatedRowGroup;

/**
 * Multi-Hierarchy account summary — an alternative report structure that supports
 * multiple CoA hierarchies in a single report. Unlike standard `IAccountSummary`,
 * row groups are `MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup`
 * and column groups are `MultiHierarchyColumnDataSource | MultiHierarchyCalculatedColumnGroup`.
 *
 * The `variant` discriminant (`EAccountSummaryVariant.MultiHierarchy`) is always set,
 * enabling type narrowing via `isMultiHierarchyAccountSummary()`.
 */
export type MultiHierarchyAccountSummary = Omit<
  IAccountSummary,
  'rowGroups' | 'columnGroups' | 'variant' | 'filters'
> & {
  variant: EAccountSummaryVariant.MultiHierarchy;
  rowGroups: (MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup)[];
  columnGroups: (MultiHierarchyColumnDataSource | MultiHierarchyCalculatedColumnGroup)[];
  /** View-level filters. CoA-only in the UI today; typed for the shared persisted filter union. */
  filters?: MultiHierarchyReportFilter[];
};
